"use client";

import { useState } from "react";
import { Sparkles, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAdminOverlay } from "./admin-overlay-context";
import { useSectionRegenerate } from "./use-section-regenerate";
import type { AdminSectionKey } from "./section-generators";

// Bouton flottant "régénérer cette section", posé en admin uniquement à côté
// de chaque section générée par IA sur `/[slug]`. Ouvre un dialogue avec le
// prompt (pré-rempli avec le dernier prompt réellement envoyé, sinon le
// défaut), éditable ponctuellement ici — pour un changement permanent, éditer
// `src/lib/content/ai-prompts.ts`.
export function SectionRegenerateButton({
  sectionKey,
  className = "",
}: {
  sectionKey: AdminSectionKey;
  className?: string;
}) {
  const { data } = useAdminOverlay();
  const { descriptor, isRunning, run } = useSectionRegenerate(sectionKey);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(descriptor.defaultPrompt);

  const openDialog = () => {
    setPrompt(descriptor.currentPrompt(data) || descriptor.defaultPrompt);
    setOpen(true);
  };

  const handleRegenerate = async () => {
    const ok = await run(prompt);
    if (ok) setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        title={`Régénérer : ${descriptor.label}`}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-line bg-paper-pure text-ink/60 shadow-sm transition-colors hover:border-gold hover:text-gold ${className}`}
      >
        <Sparkles className="h-3.5 w-3.5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Régénérer : {descriptor.label}</DialogTitle>
            <DialogDescription>
              Modifie le prompt si besoin, puis régénère. La page se met à jour juste après.
              {descriptor.note && <span className="mt-1 block text-amber-600">{descriptor.note}</span>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Prompt</label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPrompt(descriptor.defaultPrompt)}
                disabled={prompt === descriptor.defaultPrompt}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Réinitialiser
              </Button>
            </div>
            <Textarea
              rows={14}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="font-mono text-xs"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isRunning}>
              Annuler
            </Button>
            <Button type="button" onClick={handleRegenerate} disabled={isRunning}>
              {isRunning ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1.5" />
              )}
              Régénérer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
